import type { CncQuoteFields, RfqPackage } from '../types';
import { computeHandCutVsCnc, type HandCutVsCncInputs } from './handCutVsCncCalc';

export function computeCncQuoteTotal(flatFee: number, ratePerBdft: number, boardFeet: number): number {
  return Math.round((flatFee + ratePerBdft * boardFeet) * 100) / 100;
}

export function buildCncQuoteFields(
  flatFee: number,
  ratePerBdft: number,
  boardFeet: number,
  leadTimeWeeks?: number,
  notes?: string,
): CncQuoteFields {
  const totalEstimate = computeCncQuoteTotal(flatFee, ratePerBdft, boardFeet);
  return {
    flatFee,
    ratePerBdft,
    leadTimeWeeks: leadTimeWeeks ?? undefined,
    notes: notes?.trim() || undefined,
    totalEstimate,
  };
}

export function cncQuoteFromSubmissionFields(
  fields: CncQuoteFields,
  boardFeet: number,
): HandCutVsCncInputs {
  return {
    boardFeet,
    handCutMode: 'directBdft',
    crewCount: 0,
    cutDays: 0,
    hoursPerDay: 0,
    laborRatePerHour: 0,
    handCutDollarPerBdft: 0,
    cncDollarPerBdft: fields.ratePerBdft ?? 0,
    cncFlatFee: fields.flatFee ?? 0,
    handOverhead: 0,
    handConsumables: 0,
    handDieselGallons: 0,
    handDieselPricePerGal: 0,
    handShipping: 0,
    cncOverhead: 0,
    cncConsumables: 0,
    cncDieselGallons: 0,
    cncDieselPricePerGal: 0,
    cncShipping: 0,
  };
}

export function formatCncQuoteSummary(fields: CncQuoteFields, boardFeet: number): string {
  const hasFlat = typeof fields.flatFee === 'number' && fields.flatFee > 0;
  const hasRate = typeof fields.ratePerBdft === 'number' && fields.ratePerBdft > 0;
  const parts = [
    hasFlat ? `$${Number(fields.flatFee).toFixed(2)} flat` : null,
    hasRate ? `$${Number(fields.ratePerBdft).toFixed(2)}/bdft × ${boardFeet} bf` : null,
    fields.leadTimeWeeks != null ? `${fields.leadTimeWeeks} wk lead` : null,
  ].filter(Boolean);

  // Lump-sum quotes (manual upload or a split with no rate) store totalEstimate
  // only. Rate math is $0 in that case — don't render "= $0.00".
  if (!hasFlat && !hasRate) return parts.join(' + ');

  const inputs = cncQuoteFromSubmissionFields(fields, boardFeet);
  const result = computeHandCutVsCnc(inputs);
  if (!(result.cncTotal > 0)) return parts.join(' + ');
  return `${parts.join(' + ')} = $${result.cncTotal.toFixed(2)}`;
}

export function packageBoardFeet(pkg: RfqPackage): number {
  return pkg.cncPackagePayload?.boardFeet ?? 0;
}
